import React, { useState } from 'react';
import SEO from '@/components/seo/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { AlertCircle, CheckCircle2, Building, MapPin, ArrowRight } from 'lucide-react';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { getSignupUrl } from '@/lib/appLinks';
import { trackSignupIntent } from '@/lib/tracking';

const CGP2025Page: React.FC = () => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  
  const appSignUpUrl = getSignupUrl();

  const handleAnswer = (key: string, value: string) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  const nextStep = () => setStep(prev => prev + 1);
  const reset = () => { setStep(1); setAnswers({}); };

  const getResult = () => {
    if (answers['state_authority'] === 'state') {
        return {
            status: 'Unlikely Affected',
            color: 'text-gray-600',
            bg: 'bg-gray-100 dark:bg-gray-800',
            msg: "Your state has its own permitting authority (e.g., TCEQ, SWRCB). The 2025 EPA modification primarily affects areas where the EPA is the direct permitting authority.",
            action: "Check your state's specific general permit for updates."
        };
    }
    if (answers['federal_land'] === 'yes') {
        return {
            status: 'New Coverage Required',
            color: 'text-red-600',
            bg: 'bg-red-100 dark:bg-red-900/30',
            msg: "Effective April 8, 2025: The EPA modification explicitly expands CGP coverage to 'Lands of Exclusive Federal Jurisdiction.' Your project now requires direct EPA coverage.",
            action: "File an eNOI via EPA's NeT system before April 8."
        };
    }
    return {
        status: 'Standard EPA Coverage',
        color: 'text-blue-600',
        bg: 'bg-blue-100 dark:bg-blue-900/30',
        msg: "You are likely covered under the standard EPA CGP terms. The 2025 modification clarifies definitions but likely won't change your workflow.",
        action: "Maintain your standard SWPPP and inspection frequency."
    };
  };

  const result = getResult();

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-blue-500/20">
      <SEO 
        title="2025 EPA CGP Modification Checker - Federal Lands Rule"
        description="Does the 2025 EPA Construction General Permit modification affect your project? Use this free tool to check applicability for Federal Lands. Effective April 8, 2025."
        canonical="/resources/2025-cgp-update"
      />

      <div className="bg-muted/30 py-20 border-b border-border">
        <div className="container px-4 text-center max-w-3xl mx-auto">
           <Breadcrumbs items={[
               { label: 'Resources', href: '/' },
               { label: '2025 Permit Check', href: '/resources/2025-cgp-update' }
           ]} />

           <div className="inline-flex items-center justify-center p-3 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full mb-6">
             <Building size={32} />
           </div>
           <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
             2025 Permit Applicability Check
           </h1>
           <p className="text-xl text-muted-foreground">
             Effective April 8, 2025: The EPA Construction General Permit (CGP) now explicitly covers "Lands of Exclusive Federal Jurisdiction."
           </p>
        </div>
      </div>

      <div className="container px-4 py-16 max-w-2xl mx-auto">
        <Card className="border-border shadow-lg mb-24">
            <CardHeader>
                <CardTitle>Impact Assessment</CardTitle>
                <CardDescription>Answer 2 questions to determine your regulatory status.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
                
                {/* Step 1 */}
                <div className={`space-y-4 transition-opacity duration-500 ${step === 1 ? 'opacity-100' : 'opacity-50 pointer-events-none hidden'}`}>
                    <div className="space-y-2">
                        <Label className="text-base font-semibold">1. Who handles stormwater permits in your state?</Label>
                        <p className="text-sm text-muted-foreground">Most states (TX, CA, FL, NY) manage their own. Only NM, NH, MA, and DC are directly EPA-managed for all sites.</p>
                    </div>
                    <RadioGroup onValueChange={(val) => handleAnswer('state_authority', val)}>
                        <div className="flex items-center space-x-2 border p-4 rounded-lg cursor-pointer hover:bg-muted/50">
                            <RadioGroupItem value="state" id="r1" />
                            <Label htmlFor="r1" className="cursor-pointer flex-grow">State Agency (e.g. TCEQ, FDEP, DEQ)</Label>
                        </div>
                        <div className="flex items-center space-x-2 border p-4 rounded-lg cursor-pointer hover:bg-muted/50">
                            <RadioGroupItem value="epa" id="r2" />
                            <Label htmlFor="r2" className="cursor-pointer flex-grow">Direct EPA (NM, NH, MA, DC, Puerto Rico)</Label>
                        </div>
                    </RadioGroup>
                    <Button onClick={nextStep} disabled={!answers['state_authority']} className="w-full">Next &rarr;</Button>
                </div>

                {/* Step 2 */}
                {step >= 2 && (
                    <div className={`space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500 ${step === 2 ? '' : 'hidden'}`}>
                        <div className="space-y-2">
                            <Label className="text-base font-semibold">2. Is your project located on Federal Land?</Label>
                            <p className="text-sm text-muted-foreground">Examples: Military bases, National Parks, Federal Courthouses, or other "Lands of Exclusive Federal Jurisdiction".</p>
                        </div>
                        <RadioGroup onValueChange={(val) => handleAnswer('federal_land', val)}>
                            <div className="flex items-center space-x-2 border p-4 rounded-lg cursor-pointer hover:bg-muted/50">
                                <RadioGroupItem value="yes" id="r3" />
                                <Label htmlFor="r3" className="cursor-pointer flex-grow">Yes, Federal Property</Label>
                            </div>
                            <div className="flex items-center space-x-2 border p-4 rounded-lg cursor-pointer hover:bg-muted/50">
                                <RadioGroupItem value="no" id="r4" />
                                <Label htmlFor="r4" className="cursor-pointer flex-grow">No, Private or State/Local Public Land</Label>
                            </div>
                        </RadioGroup>
                        <div className="flex gap-3">
                             <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                             <Button onClick={nextStep} disabled={!answers['federal_land']} className="w-full">See Results</Button>
                        </div>
                    </div>
                )}

                {/* Result */}
                {step === 3 && (
                    <div className="space-y-6 animate-in zoom-in-95 duration-500">
                        <div className={`p-6 rounded-xl border ${result.bg} border-transparent text-center`}>
                            {result.status === 'New Coverage Required' ? <AlertCircle className={`mx-auto h-12 w-12 mb-4 ${result.color}`} /> : <CheckCircle2 className={`mx-auto h-12 w-12 mb-4 ${result.color}`} />}
                            <h3 className={`text-2xl font-bold mb-2 ${result.color}`}>{result.status}</h3>
                            <p className="text-foreground/80 font-medium mb-4">{result.msg}</p>
                            <div className="bg-background/50 p-3 rounded-lg text-sm inline-block border border-black/5 dark:border-white/10">
                                <strong>Recommendation:</strong> {result.action}
                            </div>
                        </div>

                        <div className="bg-muted/30 p-6 rounded-xl border border-border">
                            <h4 className="font-bold mb-2 flex items-center gap-2">
                                <MapPin size={18} className="text-primary"/> Automate Your NOI
                            </h4>
                            <p className="text-sm text-muted-foreground mb-4">
                                Whether you answer to the State or the EPA, RunOffTrack helps you generate the maps and SWPPP narrative required for your Notice of Intent.
                            </p>
                            <Button size="lg" className="w-full" asChild>
                                <a href={appSignUpUrl} onClick={() => trackSignupIntent('cgp_assessment_result')}>Start Your SWPPP <ArrowRight className="ml-2 h-4 w-4"/></a>
                            </Button>
                        </div>
                        
                        <div className="text-center">
                            <Button variant="ghost" onClick={reset} size="sm">Start Over</Button>
                        </div>
                    </div>
                )}

            </CardContent>
        </Card>

        {/* SEO CONTENT SECTION */}
        <div className="prose dark:prose-invert max-w-3xl mx-auto pt-8 border-t border-border">
            <h2>The Federal Lands Gap</h2>
            <p>
                In the United States, stormwater permitting is a patchwork. Most states (47 of them) are "authorized" to manage their own NPDES programs. Texas has TCEQ, California has SWRCB, etc.
            </p>
            <p>
                However, there are pockets of land within these states that fall under exclusive federal jurisdiction. These include many military installations (e.g., Fort Hood, Camp Pendleton) and certain federal buildings.
            </p>
            <p>
                Historically, there was confusion: Does a contractor on a Texas military base file with TCEQ or the EPA?
            </p>

            <h3>The 2025 Modification Explained</h3>
            <p>
                The EPA's modification to the 2022 CGP clarifies that <strong>the EPA retains permitting authority</strong> for these lands. This ensures consistent federal oversight regardless of the surrounding state's regulations.
            </p>
            
            <h3>What you need to do</h3>
            <ol>
                <li><strong>Check Your Jurisdiction:</strong> Ask your project owner or contracting officer if the site is considered "Exclusive Federal Jurisdiction."</li>
                <li><strong>File Correctly:</strong> If yes, you must use the EPA's NeT system to file your NOI, not the state portal.</li>
                <li><strong>Update SWPPP:</strong> Ensure your SWPPP cites the 2022 EPA CGP, not the state general permit.</li>
            </ol>
        </div>
      </div>
    </div>
  );
};

export default CGP2025Page;
